import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './lib/auth/msal-config';
import { Toaster } from 'sonner';

import { lazy, Suspense } from 'react';
import AppShell from './components/layout/AppShell';
import ProtectedRoute from './components/auth/ProtectedRoute';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const TrailsPage = lazy(() => import('./pages/settings/TrailsPage'));
const PackagesPage = lazy(() => import('./pages/PackagesPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const StaffPage = lazy(() => import('./pages/settings/StaffPage'));
const SystemConfigPage = lazy(() => import('./pages/settings/SystemConfigPage'));
const AuditLogPage = lazy(() => import('./pages/settings/AuditLogPage'));
const BookingConfigPage = lazy(() => import('./pages/settings/BookingConfigPage'));
const FaqPage = lazy(() => import('./pages/settings/FaqPage'));
const TrailResourcesPage = lazy(() => import('./pages/settings/TrailResourcesPage'));
const MediaPipelinePage = lazy(() => import('./pages/MediaPipelinePage'));

const msalInstance = new PublicClientApplication(msalConfig);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: 1,
    },
  },
});

function LoadingFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
        <p className="text-neutral-600">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              <Route
                element={
                  <ProtectedRoute>
                    <AppShell />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<DashboardPage />} />

                {/* Trails, Packages & Gallery */}
                <Route path="/trails" element={<TrailsPage />} />
                <Route path="/packages" element={<PackagesPage />} />
                <Route path="/gallery" element={<GalleryPage />} />

                {/* Media Pipeline */}
                <Route path="/media-pipeline" element={<MediaPipelinePage />} />

                {/* Settings */}
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/staff" element={<StaffPage />} />
                <Route path="/settings/config" element={<SystemConfigPage />} />
                <Route path="/settings/audit" element={<AuditLogPage />} />
                <Route path="/settings/booking" element={<BookingConfigPage />} />
                <Route path="/settings/faq" element={<FaqPage />} />
                <Route path="/settings/trail-resources" element={<TrailResourcesPage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </QueryClientProvider>
    </MsalProvider>
  );
}

export default App;
